import { useState, useMemo } from 'react';
import { Transaction, Budget, Category, Person, Destination } from '../types/budget';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateBudget } from '../db';
import { filterTransactions, sortTransactions } from '../utils/transactionCalculations';

type SortField = 'category' | 'responsible' | 'description' | 'destination' | 'amount';
type SortDirection = 'asc' | 'desc';

interface UseTransactionsProps {
  budget: Budget;
}

export const useTransactions = ({ budget }: UseTransactionsProps) => {
  const [sortField, setSortField] = useState<SortField>('amount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filters, setFilters] = useState({
    category: '' as Category | '',
    responsible: '' as Person | '',
    destination: '' as Destination | '',
    amountType: '' as '' | 'income' | 'expense',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();

  const queryClient = useQueryClient();

  // Define the budget-specific query key
  const budgetId = budget.id;

  const updateBudgetMutation = useMutation({
    mutationFn: async (updatedBudget: Budget) => {
      updateBudget(updatedBudget);
      return updatedBudget;
    },
    onSuccess: () => {
      // Invalidate the specific budget query if we have an ID
      if (budgetId) {
        queryClient.invalidateQueries({ queryKey: ['budget', budgetId] });
      }

      // Invalidate general queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['defaultBudget'] });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });

      // No need to invalidate a transactions query since transactions are part of budgets
    },
  });

  const handleAddTransaction = (transactions: Array<Omit<Transaction, 'id'>>) => {
    const maxId = budget.transactions.reduce((max, t) => {
      const currentId = t.id ?? 0;
      return Math.max(max, currentId);
    }, 0);

    const newTransactions: Transaction[] = transactions.map((transaction, index) => ({
      ...transaction,
      id: maxId + index + 1,
    }));

    const newBudget = {
      ...budget,
      transactions: [...budget.transactions, ...newTransactions],
    };

    updateBudgetMutation.mutate(newBudget);
    setIsFormOpen(false);
  };

  const handleEditTransaction = (transactions: Array<Omit<Transaction, 'id'>>) => {
    if (!selectedTransaction) return;

    const transaction = transactions[0];
    const newBudget = {
      ...budget,
      transactions: budget.transactions.map((t) =>
        t.id === selectedTransaction.id ? { ...transaction, id: t.id } : t
      ),
    };

    updateBudgetMutation.mutate(newBudget);
    setSelectedTransaction(undefined);
    setIsFormOpen(false);
  };

  const handleDeleteTransaction = (transaction: Transaction) => {
    if (transaction.id === undefined) {
      console.error('Cannot delete transaction without ID');
      return;
    }

    const newBudget = {
      ...budget,
      transactions: budget.transactions.filter((t) => t.id !== transaction.id),
    };

    updateBudgetMutation.mutate(newBudget);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredSortedTransactions = useMemo(() => {
    let filtered = budget.transactions;

    // Apply search filter first
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.category.toLowerCase().includes(searchLower) ||
          t.responsible.toLowerCase().includes(searchLower) ||
          t.description.toLowerCase().includes(searchLower) ||
          t.destination.toLowerCase().includes(searchLower) ||
          t.amount.toString().includes(filters.search)
      );
    }

    // Apply other filters
    filtered = filterTransactions(filtered, {
      ...(filters.category ? { category: filters.category } : {}),
      ...(filters.responsible ? { responsible: filters.responsible } : {}),
      ...(filters.destination ? { destination: filters.destination } : {}),
      ...(filters.amountType ? { amountType: filters.amountType as 'income' | 'expense' } : {}),
    });

    return sortTransactions(filtered, sortField as keyof Transaction, sortDirection);
  }, [budget.transactions, filters, sortField, sortDirection]);

  return {
    transactions: filteredSortedTransactions,
    filters,
    setFilters,
    showFilters,
    setShowFilters,
    isFormOpen,
    setIsFormOpen,
    selectedTransaction,
    setSelectedTransaction,
    sortField,
    sortDirection,
    handleSort,
    handleAddTransaction,
    handleEditTransaction,
    handleDeleteTransaction,
  };
};
